import { Box, Checkbox } from '@mui/material';
import { useStyles } from './EvaluationView.styles';
import { useStyles as useRadioStyles } from './SingleSelect.styles';
import { Icons } from '../../../../../../icons';
import { btnType, Button } from '../../../../../atoms/button';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAssignedEvaluationState,
  selectEvaluation,
} from '../../../../../../../redux/modules/conversation/selectors';
import { getTimeForEval } from '../../../../../../../utils/CommonUtils';
import { Radio } from '../../../../../atoms/radio';
import { Input, inputType } from '../../../../../atoms/input';
import { RadioGroup, radioGroupType } from '../../../../../molecules/radio-group';
import useEvaluationHead from '../../../../../../../hooks/useEvaluationHead';
import cx from 'clsx';
import clsx from 'clsx';
import { colors } from '../../../../../../colors';
import { Text } from '../../../../../atoms/typography';
import { EVALUATION_QUESTION_TYPES } from '../../../../../../../constants/CommonConstants';
import ElectroCardio from '../../../../../../icons/lotties/lottieIcons/ElectroCardio';
import ThinkingAnimated from '../../../../../../icons/lotties/lottieIcons/ThinkingAnimated';
import FireWorks from '../../../../../../icons/lotties/lottieIcons/FireWorks';
import { Menu } from '../../../../../molecules/menu';
import MedicalFile from '../../../../../../icons/lotties/lottieIcons/MedicalFile';
import QuestionnaireAnimated from '../../../../../../icons/lotties/lottieIcons/QuestionnaireAnimated';
import LottieQuestionaire from '../../../../../../icons/lotties/lottieIcons/Questionaire';
import { Badge } from '../../../../../atoms/badge';
import { badgeType, badgeStyle } from '../../../../../atoms/badge/Badge.types';

const EvaluationCover = ({
                           currentEvaluation,
                           promptsVisible,
                           expandedEvaluation,
                           scrollToTop,
                           setScrollToTop,
                         }) => {
  const ref = useRef(null);
  const classes = useStyles();

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const getSubtitle = () => {
    let subtitle = '';
    const duration = currentEvaluation?.evalDurationMinutes || 0;
    if (currentEvaluation?.evaluationStats?.dctCount > 0) {
      subtitle += `${currentEvaluation?.evaluationStats?.dctCount} Sections`;
      if (duration > 0) {
        subtitle += ` • ${getTimeForEval(duration)}`;
      }
    } else {
      if (duration > 0) {
        subtitle += `${getTimeForEval(duration)}`;
      }
    }
    if (subtitle === '') {
      return null;
    }
    return subtitle;
  };
  const subtitle = getSubtitle();
  return (
    <Box className={classes.evaluationsBox} ref={ref}>
      <Box className={classes.welcomeFrame}>
        <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
          <p className={classes.sectionTitle}>
            {currentEvaluation?.name}
          </p>
          <p className={classes.sectionSubtitle}>
            {subtitle}
          </p>
        </Box>
        <MedicalFile className={classes.animatedIcon} />
      </Box>
      {promptsVisible && currentEvaluation?.prompts?.length > 0 && (
        <Box className={classes.askBox}>
          <Box className={classes.contentHeader}>
            <Icons glyph={'chat-outlined'} className={classes.askIcon} />
            <p className={classes.firstLine}>
              You may want to <span style={{ fontWeight: 700, color: colors.success600 }}>say</span> things like:
            </p>
          </Box>
          <Box className={classes.askContent}>
            {currentEvaluation?.prompts?.map((prompt, index) => (
              <Box key={index} className={classes.instruction} sx={{ backgroundColor: colors.success100 }}>
                {prompt}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {expandedEvaluation?.dcts?.length > 0 && (
        <Box className={classes.dctListBox}>
          {expandedEvaluation?.dcts?.map((dct, index) => {
            const filteredCB = expandedEvaluation?.contentBlocks?.filter(
              el => el.dctId === dct.dctId && el.basicInfo.type !== 'dct',
            );
            const filteredRequiredCB = filteredCB?.filter(el => el.required);
            return (
              <Box className={classes.dctCard}>
                <Box width={40} maxWidth={40} maxHeight={40}>
                  <LottieQuestionaire
                    className={classes.lottieIcon}
                    autoplay={false}
                    initialSegment={[0, 0]}
                  />
                </Box>
                <Box display='flex' flexDirection='column' gap='2px'>
                  <span className={classes.dctName}>{`Section ${index + 1}: ${dct.name}`}</span>
                  <span className={classes.dctProgress}>{`${filteredCB?.length} Questions • ${
                    filteredRequiredCB?.length
                  } Required ${
                    dct.dctDurationMinutes ? `• ${getTimeForEval(dct.dctDurationMinutes)}` : ''
                  }`}</span>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const FullPageQuestion = ({
                            currentBlock,
                            cb,
                            viewKey,
                            promptsVisible,
                            aiMode,
                            setInputFocused,
                            profileElements,
                            dctPreviousResponses,
                            bodyRef,
                            expandedEvaluation,
                          }) => {
  const filteredCB = expandedEvaluation?.contentBlocks?.find(el => el.cbId === cb.cbId);
  let cbRelatedProfileElements = filteredCB?.relatedProfileElements ?? [];
  const hasProfileElementValues = cbRelatedProfileElements.some(element => {
    return profileElements && profileElements[element]?.length > 0;
  });
  if (hasProfileElementValues) {
    cbRelatedProfileElements = cbRelatedProfileElements
      .map(element => {
        if (profileElements && profileElements[element]?.length > 0) {
          return {
            key: element,
            value: profileElements[element][0],
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  const classes = useStyles();
  const { updateValues } = useEvaluationHead();
  const lastResponse =
    dctPreviousResponses &&
    dctPreviousResponses.length > 0 &&
    dctPreviousResponses.find(response => response.cbId === cb.cbId);
  const dispatchUserResponse = value => {
    updateValues({ id: currentBlock.cbId, value, blockId: cb.cbId });
  };

  const handleTextInputChange = e => {
    const newValue = e.target.value.trimStart();
    dispatchUserResponse(newValue);
  };

  const handleSelectChange = selectedValue => {
    dispatchUserResponse(selectedValue);
  };

  const usePreviousResponse = () => {
    if (
      cb.cbType === EVALUATION_QUESTION_TYPES.TEXT_INPUT ||
      cb.cbType === EVALUATION_QUESTION_TYPES.RATING_SCALE
    ) {
      dispatchUserResponse(lastResponse.answers[0]?.value);
    } else if (
      cb.cbType === EVALUATION_QUESTION_TYPES.SINGLE_SELECT ||
      cb.cbType === EVALUATION_QUESTION_TYPES.MULTI_SELECT
    ) {
      const choices = cb?.choices
        .filter(ch => {
          return lastResponse.answers.find(el => el.value === ch?.choice);
        })
        .map(ch => ch.choice);
      if (choices.length > 0) {
        if (cb.cbType === EVALUATION_QUESTION_TYPES.SINGLE_SELECT) {
          dispatchUserResponse(choices[0]);
        } else {
          choices.forEach(dispatchUserResponse);
        }
      }
    }
  };
  const singleSelectChoices = cb.ratingScale
    ? cb.ratingScale.values.map(v => ({
      label: String(v),
      value: String(v),
    }))
    : cb.choices?.map(item => ({
      label: item.choice,
      value: item.choice,
    }));
  const textInputValue =
    cb &&
    cb.cbType === 'text-input' &&
    cb.userResponse &&
    cb.userResponse.userResponseList.length > 0 &&
    cb.userResponse.userResponseList[0];
  return (
    <Box className={classes.fullPageQuestionBox} key={viewKey}>
      <Box display='flex' flexDirection='column' gap='4px'>
        <p className={classes.questionTitle}>
          {cb.cbType !== 'provider-message' ? cb.text : 'Provider Message'}
          {cb.required && <span className={classes.requiredText}>Required</span>}
        </p>
        {cb.subtitle && cb.subtitle !== '' && (
          <p className={classes.questionSubtitle}>{cb.subtitle}</p>
        )}
      </Box>
      {promptsVisible && cb.prompt && cb.prompt?.trim() !== '' && (
        <Box className={classes.promptContainer}>
          <Icons glyph={'question-outlined'} className={classes.promptIcon} />
          <p className={classes.promptValue}>{cb.prompt}</p>
        </Box>
      )}
      {hasProfileElementValues && (
        <Box className={classes.profileElementsBox}>
          <Box className={classes.profileElementsTitleBox}>
            <Box className={classes.profileElementsTitle}>What we already know:</Box>
            <Icons glyph={'alert-outlined'} className={classes.sayIcon} />
          </Box>
          <Box className={classes.profileElementsList}>
            <ul>
              {cbRelatedProfileElements.map(element => (
                <li className={classes.profileElement}>
                  <strong>{element.key}: </strong>
                  {element.value}
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      )}
      {(!aiMode || cb.forceManualEntry) && (
        <>
          {cb.choices &&
            (cb.cbType === 'single-select' ||
              cb.cbType === 'multi-select' ||
              cb.cbType === 'rating-scale') && (
              <Box className={classes.choicesContainer}>
                {cb.cbType === 'multi-select' ? (
                  cb.choices.map((item, index) => (
                    <MultiSelectOption
                      fullPage
                      answerTitle={item.choice}
                      value={item.choice}
                      key={index}
                      currentBlock={cb}
                      onChange={handleSelectChange}
                      scrollRef={bodyRef}
                    />
                  ))
                ) : (
                  <SingleSelectOptions
                    fullPage
                    options={singleSelectChoices}
                    currentBlock={cb}
                    onChange={handleSelectChange}
                    scrollRef={bodyRef}
                    selectedIndex={-1}
                  />
                )}
              </Box>
            )}
          {cb.cbType === 'text-input' && (
            <>
              <Input
                fullWidth
                variant={inputType.DEFAULT}
                placeholder='Enter your answer'
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                multiline
                onChange={handleTextInputChange}
                minRows={3}
                maxRows={3}
                value={textInputValue || ''}
              />
              {cb.helperText && cb.helperText !== '' && <p className={classes.helperText}>{cb.helperText}</p>}
            </>
          )}
          {lastResponse && (
            <Box className={classes.useAllBoxFP}>
              <Box className={classes.prevResponseDeets}>
                <p className={classes.prevTitle}>Previous response:</p>
                <p className={classes.prevResponse}>
                  {lastResponse.answers.map(ans => ans.value).join(', ')}
                </p>
              </Box>
              <Button
                variant={btnType.OUTLINE}
                className={classes.useBtn}
                onClick={() => {
                  usePreviousResponse();
                }}
              >
                Use
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const DCTCover = ({
                    currentBlock,
                    promptsVisible,
                    dctPreviousResponses,
                    dctSelectedResponses,
                    onPreviousResponseSelected,
                    eligibleForAutodoc,
                    aiMode,
                    listHelper,
                    profileElements,
                    toggleAiMode,
                    setInputFocused,
                    expandedEvaluation,
                    useAllPreviousResponses,
                    scrollToTop,
                    setScrollToTop,
                  }) => {
  const ref = useRef(null);
  const classes = useStyles();
  const totalQuestions = currentBlock.children?.length;
  const requiredQuestions = currentBlock.children?.filter(q => q.required).length;
  const hasPreviousResponses = dctPreviousResponses && dctPreviousResponses.length > 0;
  const responsesText = currentBlock.fullPage
    ? 'Please make sure to ask all the questions here:'
    : hasPreviousResponses
      ? 'Select which previous responses to use:'
      : 'Upcoming questions and typical responses:';
  const childQuestions = currentBlock.children.filter(
    cb => cb.cbType !== 'education' && cb.cbType !== 'exercise' && cb.isAttemptable,
  );
  const allPreviousNormal =
    hasPreviousResponses &&
    dctPreviousResponses.every(response => {
      const childCb = childQuestions.find(cb => cb.cbId === response.cbId);
      const riskyChoices = childCb?.choices?.filter(ans => ans.riskFactor).map(ans => ans.value) || [];
      return response.answers.every(ans => !riskyChoices.includes(ans.value));
    });
  const allPreviousRisky =
    hasPreviousResponses &&
    dctPreviousResponses.every(response => {
      const childCb = childQuestions.find(cb => cb.cbId === response.cbId);
      const riskyChoices = childCb?.choices?.filter(ans => ans.riskFactor).map(ans => ans.value) || [];
      return response.answers.some(ans => riskyChoices.includes(ans.value));
    });

  const handleDctQuestionBoxClick = (cbId, previousResponse, typicalResponse) => {
    const isSelected = dctSelectedResponses.some(response => response.cbId === cbId);
    if (isSelected) {
      onPreviousResponseSelected(cbId, []);
    } else {
      if (previousResponse) {
        onPreviousResponseSelected(cbId, previousResponse.answers);
      } else if (typicalResponse) {
        onPreviousResponseSelected(cbId, [{ value: typicalResponse.choice }]);
      }
    }
  };
  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const getSectionNumber = dctId => {
    const index = listHelper.getDcts().findIndex(dct => dct.dctId === dctId);
    if (index !== -1) {
      return index + 1;
    }
    return index;
  };
  return (
    <Box className={classes.dctCover} ref={ref}>
      {currentBlock.fullPage ? (
        <Box className={classes.welcomeFrame}>
          <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
            <p className={classes.sectionTitle}>
              Section {getSectionNumber(currentBlock?.dctId)}: {currentBlock?.text}
            </p>
            <p className={classes.sectionSubtitle}>
              {totalQuestions} Question{totalQuestions > 1 ? 's' : ''} {requiredQuestions > 0 ? `• ${requiredQuestions} Required` : ''}
            </p>
          </Box>
          <QuestionnaireAnimated className={classes.animatedIcon} />
        </Box>
      ) : (
        <Box className={classes.titleBox}>
          <QuestionnaireAnimated className={classes.animatedIcon} />
          <Box display='flex' flexDirection='column' gap='4px'>
            <p className={classes.sectionTitle}>
              Section {getSectionNumber(currentBlock.dctId)}: {currentBlock.text}
            </p>
            <p className={clsx([classes.textAlignCenter, classes.sectionSubtitle])}>
              {totalQuestions} Question{totalQuestions > 1 ? 's' : ''} {requiredQuestions > 0 ? `• ${requiredQuestions} Required` : ''}{' '}
              {currentBlock.dctDurationMinutes && `• ${getTimeForEval(currentBlock.dctDurationMinutes)}`}
            </p>
          </Box>
        </Box>
      )}

      {promptsVisible && currentBlock.prompt && currentBlock.prompt !== '' && (
        <Box className={[classes.askBox, classes.bgWhite]}>
          <Box className={classes.contentHeader}>
            <Icons glyph={'chat-outlined'} className={classes.askIcon} />
            <p className={classes.firstLine}>
              You may want to <span style={{ fontWeight: 700, color: colors.success600 }}>say</span> things like:
            </p>
          </Box>
          <Box className={classes.askContent}>
            <Box className={classes.instruction} sx={{ backgroundColor: colors.success100 }}>
              {currentBlock.prompt}
            </Box>
          </Box>
        </Box>
      )}
      <Box className={classes.fullPageQuestionsBox}>
        {currentBlock.fullPage ? (
          <>
            <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
              <p className={classes.fullPageHeading}>
                Please make sure to {eligibleForAutodoc ? 'ask' : 'fill out responses manually for'} all the questions
                here.
              </p>
              <p className={classes.fullPageSubheading}>
                The responses
                are {eligibleForAutodoc ? 'being automatically captured for you' : 'not being captured automatically because AI analysis is deactivated'}.
              </p>
            </Box>
            {childQuestions.map(cb => {
              return (
                <FullPageQuestion
                  expandedEvaluation={expandedEvaluation}
                  viewKey={cb.cbId}
                  key={cb.cbId}
                  currentBlock={currentBlock}
                  profileElements={profileElements}
                  cb={cb}
                  promptsVisible={promptsVisible}
                  aiMode={eligibleForAutodoc && aiMode}
                  dctPreviousResponses={dctPreviousResponses}
                  setInputFocused={setInputFocused}
                />
              );
            })}
          </>
        ) : (
          <>
            {childQuestions.map(cb => {
              const previousResponse = hasPreviousResponses
                ? dctPreviousResponses.find(prev => prev.cbId === cb.cbId)
                : null;
              const typicalResponse =
                !hasPreviousResponses && cb.choices ? cb.choices.find(choice => !choice.riskFactor) : null;
              const riskyChoices = cb.choices
                ? cb.choices.filter(choice => choice.riskFactor).map(choice => choice.choice)
                : [];
              const safeResponse =
                previousResponse && previousResponse.answers.every(ans => !riskyChoices.includes(ans.value));
              const riskyResponse =
                previousResponse && previousResponse.answers.some(ans => riskyChoices.includes(ans.value));
              return (
                <Box
                  className={clsx(classes.dctQuestionBox, {
                    [classes.dctQuestionBoxSelected]: !!typicalResponse || !!previousResponse,
                  })}
                  key={cb.cbId}
                  onClick={() => handleDctQuestionBoxClick(cb.cbId, previousResponse, typicalResponse)}
                >
                  <Box className={classes.questionAnswer}>
                    <p className={classes.cardTitle}>{cb.text}</p>
                    <p
                      className={clsx(classes.cardSubtitle, {
                        [classes.safeResponse]: safeResponse,
                        [classes.riskyResponse]: riskyResponse,
                        [classes.nonSafeNonRiskyResponse]:
                        previousResponse && !safeResponse && !riskyResponse,
                      })}
                    >
                      {!!previousResponse ? (
                        <>{previousResponse.answers.map(ans => ans.value).join(', ')}</>
                      ) : (
                        <>
                          {typicalResponse
                            ? typicalResponse.choice
                            : `No ${hasPreviousResponses ? 'previous' : 'typical'} response`}
                        </>
                      )}
                    </p>
                  </Box>
                  {(!!typicalResponse || !!previousResponse) && (
                    <Radio
                      checked={dctSelectedResponses.some(response => response.cbId === cb.cbId)}
                      /* onChange={e => {
                        if (previousResponse) {
                          onPreviousResponseSelected(cb.cbId, previousResponse.answers);
                        } else if (typicalResponse) {
                          onPreviousResponseSelected(cb.cbId, [{ value: typicalResponse.choice }]);
                        }
                      }} */
                      onClick={() => handleDctQuestionBoxClick(cb.cbId, previousResponse, typicalResponse)}
                    />
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Box>
      {hasPreviousResponses && !currentBlock.fullPage && (
        <Box className={classes.useAllBox}>
          <Box className={classes.prevResponseDeets}>
            <p className={classes.prevTitle}>Previous responses:</p>
            <p
              className={clsx(classes.prevResponse, {
                [classes.allSafeResponses]: allPreviousNormal,
                [classes.allRiskyResponses]: allPreviousRisky,
                [classes.allNonSafeNonRiskyResponses]: !allPreviousNormal && !allPreviousRisky,
              })}
            >
              {allPreviousNormal
                ? 'All in normal range'
                : allPreviousRisky
                  ? 'All in risky range'
                  : 'Some are in the typical range'}
            </p>
          </Box>
          <Button
            variant={btnType.OUTLINE}
            className={classes.useBtn}
            onClick={() => {
              useAllPreviousResponses(allPreviousNormal, allPreviousRisky, false);
            }}
          >
            Use
          </Button>
        </Box>
      )}
      {currentBlock.fullPage ? (
        <AIDocHint
          eligibleForAutodoc={eligibleForAutodoc}
          aiMode={aiMode}
          toggleAiMode={toggleAiMode}
          forceManualEntry={childQuestions.some(cb => cb.forceManualEntry)}
        />
      ) : (
        <Box className={classes.notSelectionBox}>
          <p className={classes.footerTitle}>Anything not selected will be asked again</p>
        </Box>
      )}
    </Box>
  );
};

const SingleSelectOptions = ({ options, currentBlock, onChange, selectedIndex, scrollRef, fullPage }) => {
  const classes = useRadioStyles({ currentIndex: selectedIndex, choices: options });
  const ref = useRef(null);

  let value = null;
  if (currentBlock?.userResponse?.userResponseList?.length > 0) {
    const response = currentBlock.userResponse.userResponseList[0];
    value = options.find(op => op.value == response);
  } else if (Array.isArray(currentBlock?.userResponse) && currentBlock?.userResponse?.length > 0) {
    const response = currentBlock?.userResponse[0];
    value = options.find(op => op.value == response);
  }

  useEffect(() => {
    if (ref?.current && scrollRef?.current && selectedIndex > -1) {
      scrollRef.current.scrollTop =
        ref.current.offsetTop + selectedIndex * 60 - scrollRef.current.offsetTop - 60;
    }
  }, [scrollRef, selectedIndex, ref]);

  return (
    <Box className={fullPage ? classes.radioGroupRootFP : classes.radioGroupRoot} ref={ref}>
      <RadioGroup
        variant={radioGroupType.VERTICAL}
        options={options}
        value={value?.value || null}
        onKeyDown={e => {
          e.preventDefault();
        }}
        onChange={(e, v) => {
          onChange(v);
        }}
      />
    </Box>
  );
};

const MultiSelectOption = ({
                             answerTitle,
                             value,
                             onChange,
                             currentBlock,
                             focusedChoice,
                             scrollRef,
                             fullPage,
                           }) => {
  const classes = useStyles();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && scrollRef?.current) {
      if (focusedChoice) {
        scrollRef.current.scrollTo({
          top: ref.current.offsetTop - 291,
          behavior: 'smooth',
        });
      }
    }
  }, [scrollRef, currentBlock, focusedChoice, ref]);

  let isChecked = false;
  if (
    currentBlock.userResponse?.userResponseList?.length > 0 &&
    currentBlock.userResponse.userResponseList.includes(value)
  ) {
    isChecked = true;
  } else if (
    Array.isArray(currentBlock?.userResponse) &&
    currentBlock?.userResponse?.length > 0 &&
    currentBlock?.userResponse?.includes(value)
  ) {
    isChecked = true;
  }
  return (
    <Box
      className={cx({
        [classes.focusedChoice]: focusedChoice,
        [classes.multiSelectBox]: !fullPage,
        [classes.multiSelectBoxFP]: fullPage,
      })}
      onClick={() => onChange(value)}
      ref={ref}
    >
      <Text className={cx(classes.multiSelectLabel)}>{answerTitle}</Text>
      <Checkbox
        checked={isChecked}
        value={value}
        className={classes.checkbox}
        checkedIcon={
          <Icons
            glyph='checkbox-outlined'
            color={colors.primary}
            className={classes.adjustedRightSectionCheckIcon}
          />
        }
        icon={
          <Icons
            glyph='checkbox-blank-outlined'
            color={colors.neutral400}
            className={classes.adjustedRightSectionCheckIcon}
          />
        }
      />
    </Box>
  );
};

const Question = ({
                    currentBlock,
                    promptsVisible,
                    aiMode,
                    toggleAiMode,
                    expandedEvaluation,
                    listHelper,
                    currentEvaluation,
                    setInputFocused,
                    bodyRef,
                    selectedIndex,
                    leftTrigger,
                    rightTrigger,
                    profileElements,
                    eligibleForAutodoc,
                    previousResponses,
                    dispatchUserResponse,
                    usePreviousResponse,
                    scrollToTop,
                    setScrollToTop,
                  }) => {
  const classes = useStyles();
  const ref = useRef(null);
  const getSectionNumber = dctId => {
    const index = listHelper.getDcts().findIndex(dct => dct.dctId === dctId);
    if (index !== -1) {
      return index + 1;
    }
    return index;
  };
  let cbRelatedProfileElements = currentBlock?.relatedProfileElements ?? [];
  const hasProfileElementValues = cbRelatedProfileElements.some(element => {
    return profileElements && profileElements[element]?.length > 0;
  });
  const dctBlock = currentBlock?.dctId && expandedEvaluation?.contentBlocks?.find(el => el.dctId === currentBlock.dctId && el.basicInfo?.type === 'dct');
  const dct = dctBlock && expandedEvaluation?.dcts?.find(d => d.dctId === dctBlock.dctId);
  const totalQuestions = dct?.contentBlocks?.length;
  const requiredQuestions = dct?.contentBlocks?.map(cb => {
    return expandedEvaluation?.contentBlocks?.find(el => el.cbId === cb);
  }).filter(q => q.required).length;
  if (hasProfileElementValues) {
    cbRelatedProfileElements = cbRelatedProfileElements
      .map(element => {
        if (profileElements && profileElements[element]?.length > 0) {
          return {
            key: element,
            value: profileElements[element][0],
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  const chooseText =
    currentBlock.cbType === 'single-select'
      ? 'Choose the best option'
      : currentBlock.cbType === 'multi-select'
        ? 'Choose all that apply:'
        : null;

  const handleTextInputChange = e => {
    const newValue = e.target.value.trimStart();
    dispatchUserResponse(newValue);
  };

  const handleSelectChange = selectedValue => {
    dispatchUserResponse(selectedValue);
  };

  useEffect(() => {
    if (currentBlock) {
      if (currentBlock.cbType === 'rating-scale') {
        if (currentBlock.ratingScale && currentBlock.ratingScale.values.length > 0 && selectedIndex >= 0) {
          const ratingValue = Number(currentBlock.ratingScale.values[selectedIndex]);
          if (
            currentBlock.userResponse &&
            currentBlock.userResponse.userResponseList &&
            currentBlock.userResponse.userResponseList.length > 0 &&
            currentBlock.userResponse.userResponseList.includes(ratingValue)
          ) {
            dispatchUserResponse(ratingValue);
          }
        }
      } else if (currentBlock.choices && currentBlock.choices.length > 0 && selectedIndex >= 0) {
        const value = currentBlock.choices[selectedIndex].choice;
        if (
          currentBlock.userResponse &&
          currentBlock.userResponse.userResponseList &&
          currentBlock.userResponse.userResponseList.length > 0 &&
          currentBlock.userResponse.userResponseList.includes(value)
        ) {
          dispatchUserResponse(value);
        }
      }
    }
  }, [leftTrigger]);

  useEffect(() => {
    if (currentBlock) {
      if (currentBlock.cbType === 'rating-scale') {
        if (currentBlock.ratingScale && currentBlock.ratingScale.values.length > 0 && selectedIndex >= 0) {
          const ratingValue = Number(currentBlock.ratingScale.values[selectedIndex]);
          if (
            !currentBlock.userResponse ||
            !currentBlock.userResponse.userResponseList ||
            currentBlock.userResponse.userResponseList.length === 0 ||
            !currentBlock.userResponse.userResponseList.includes(ratingValue)
          ) {
            dispatchUserResponse(ratingValue);
          }
        }
      } else if (currentBlock.choices && currentBlock.choices.length > 0 && selectedIndex >= 0) {
        const value = currentBlock.choices[selectedIndex].choice;
        if (
          !currentBlock.userResponse ||
          !currentBlock.userResponse.userResponseList ||
          currentBlock.userResponse.userResponseList.length === 0 ||
          !currentBlock.userResponse.userResponseList.includes(value)
        ) {
          dispatchUserResponse(value);
        }
      }
    }
  }, [rightTrigger]);

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const textInputValue =
    currentBlock &&
    currentBlock?.cbType === 'text-input' &&
    currentBlock?.userResponse &&
    currentBlock?.userResponse?.userResponseList?.length > 0 &&
    currentBlock?.userResponse?.userResponseList[0];
  const singleSelectChoices =
    currentBlock.cbType === 'rating-scale' && currentBlock.ratingScale
      ? currentBlock.ratingScale.values.map(v => ({
        label: String(v),
        value: String(v),
      }))
      : currentBlock.cbType === 'single-select' &&
      currentBlock.choices?.map(item => ({
        label: item.choice,
        value: item.choice,
      }));
  const lastResponse =
    previousResponses &&
    previousResponses?.length > 0 &&
    previousResponses.find(response => response.cbId === currentBlock.cbId);
  const getSubtitle = () => {
    let subtitle = '';
    const duration = currentEvaluation?.evalDurationMinutes || 0;
    if (currentEvaluation?.evaluationStats?.dctCount > 0) {
      subtitle += `${currentEvaluation?.evaluationStats?.dctCount} Sections`;
      if (duration > 0) {
        subtitle += ` • ${getTimeForEval(duration)}`;
      }
    } else {
      if (duration > 0) {
        subtitle += `${getTimeForEval(duration)}`;
      }
    }
    if (subtitle === '') {
      return null;
    }
    return subtitle;
  };
  const subtitle = getSubtitle();
  return (
    <Box className={classes.evaluationsBox} ref={ref}>
      {dct ? (
        <Box className={classes.welcomeFrame}>
          <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
            <p className={classes.sectionTitle}>
              Section {getSectionNumber(currentBlock?.dctId)}: {dctBlock?.basicInfo?.text}
            </p>
            <p className={classes.sectionSubtitle}>
              {totalQuestions} Question{totalQuestions > 1 ? 's' : ''} {requiredQuestions > 0 ? `• ${requiredQuestions} Required` : ''}
            </p>
          </Box>
          <QuestionnaireAnimated className={classes.animatedIcon} />
        </Box>
      ) : (
        <Box className={classes.welcomeFrame}>
          <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
            <p className={classes.sectionTitle}>
              {currentEvaluation?.name}
            </p>
            <p className={classes.sectionSubtitle}>
              {subtitle}
            </p>
          </Box>
          <MedicalFile className={classes.animatedIcon} />
        </Box>
      )}
      <Box className={classes.questionContainer}>
        <Box className={classes.questionBox}>
          <Box display='flex' flexDirection='column' gap='4px'>
            <p className={classes.questionTitle}>
              {currentBlock.cbType !== 'provider-message' ? currentBlock.text : 'Provider Message'}
              {currentBlock.required && <span className={classes.requiredText}>Required</span>}
            </p>
            {currentBlock.subtitle && currentBlock.subtitle !== '' && (
              <p className={classes.questionSubtitle}>{currentBlock.subtitle}</p>
            )}
          </Box>
          {promptsVisible && currentBlock.prompt && currentBlock.prompt !== '' && (
            <Box className={classes.promptContainer}>
              <Icons glyph={'question-outlined'} className={classes.promptIcon} />
              <p className={classes.promptValue}>{currentBlock.prompt}</p>
            </Box>
          )}
          {(!aiMode || !eligibleForAutodoc || currentBlock.forceManualEntry) && (
            <>
              <Box className={classes.questionInputBox}>
                {chooseText && <p className={classes.cbTypeDesc}>{chooseText}</p>}
                {(currentBlock.cbType === 'single-select' ||
                  currentBlock.cbType === 'multi-select' ||
                  currentBlock.cbType === 'rating-scale') && (
                  <Box className={classes.choicesContainer}>
                    {currentBlock.cbType === 'multi-select' ? (
                      currentBlock.choices.map((item, index) => (
                        <MultiSelectOption
                          answerTitle={item.choice}
                          value={item.choice}
                          key={index}
                          currentBlock={currentBlock}
                          onChange={handleSelectChange}
                          scrollRef={bodyRef}
                          focusedChoice={index === selectedIndex}
                        />
                      ))
                    ) : (
                      <SingleSelectOptions
                        options={singleSelectChoices}
                        currentBlock={currentBlock}
                        onChange={handleSelectChange}
                        scrollRef={bodyRef}
                        selectedIndex={selectedIndex}
                      />
                    )}
                  </Box>
                )}
                {currentBlock.cbType === 'text-input' && (
                  <Box className={classes.inputBox}>
                    <Input
                      fullWidth
                      variant={inputType.DEFAULT}
                      placeholder='Enter your answer'
                      onFocus={() => setInputFocused(true)}
                      onBlur={() => setInputFocused(false)}
                      multiline
                      onChange={handleTextInputChange}
                      minRows={3}
                      maxRows={3}
                      value={textInputValue || ''}
                    />
                    {currentBlock.helperText && currentBlock.helperText !== '' && (
                      <p className={classes.helperText}>{currentBlock.helperText}</p>
                    )}
                  </Box>
                )}
                {currentBlock.cbType === 'provider-message' && (
                  <Box className={cx(classes.inputBox, classes.firstLine)} textAlign='center'>
                    {currentBlock.text}
                  </Box>
                )}
              </Box>

            </>
          )}
        </Box>
      </Box>
      {lastResponse && (
        <Box className={classes.useAllBox}>
          <Box className={classes.prevResponseDeets}>
            <p className={classes.prevTitle}>Previous response:</p>
            <p className={classes.prevResponse}>
              {lastResponse.answers.map(ans => ans.value).join(', ')}
            </p>
          </Box>
          <Button
            variant={btnType.OUTLINE}
            className={classes.useBtn}
            onClick={() => {
              usePreviousResponse();
            }}
          >
            Use
          </Button>
        </Box>
      )}

      {hasProfileElementValues && (
        <Box className={classes.promptContainer}>
          <Box className={classes.profileElementsBox}>
            <Box className={classes.profileElementsTitleBox}>
              <Box className={classes.profileElementsTitle}>What we already know:</Box>
              <Icons glyph={'alert-outlined'} className={classes.sayIcon} />
            </Box>
            <Box className={classes.profileElementsList}>
              <ul>
                {cbRelatedProfileElements.map(element => (
                  <li className={classes.profileElement}>
                    <strong>{element.key}: </strong>
                    {element.value}
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        </Box>
      )}

      <AIDocHint
        eligibleForAutodoc={eligibleForAutodoc}
        aiMode={aiMode}
        toggleAiMode={toggleAiMode}
        forceManualEntry={currentBlock.forceManualEntry}
      />
    </Box>
  );
};

const AIDocHint = ({ eligibleForAutodoc, aiMode, toggleAiMode, forceManualEntry }) => {
  if (!eligibleForAutodoc || forceManualEntry) {
    return null;
  }
  const classes = useStyles();
  return aiMode ? (
    <Box className={classes.aiBox}>
      <Icons glyph='star' color='#dd0374' />
      <p className={classes.magicText}>This is being automatically captured for you.</p>
      <Button variant={btnType.TEXT} className={classes.textLink} onClick={toggleAiMode}>
        Manually select
      </Button>
    </Box>
  ) : (
    <Box className={classes.manualBox}>
      <p className={classes.magicText}>Want AI to do it for you?</p>
      <Button variant={btnType.TEXT} className={classes.textLink} onClick={toggleAiMode}>
        Back to AI documentation mode
      </Button>
    </Box>
  );
};

const EvaluationRecommendations = ({
  hasEvaluations,
  recommendations,
  onAddEvaluation,
  openEvaluationsDrawer,
  allCompleted,
  onRejectEvaluation,
}) => {
  const classes = useStyles();
  return (
    <>
      <Box className={classes.recommendationsBox}>
        {!hasEvaluations && (
          <Box display='flex' flexDirection='column' gap='4px'>
            <p className={classes.recommendationsTitle}>
              {allCompleted ? 'Other items' : 'Recommended items'}
            </p>
            {allCompleted && (
              <p className={classes.recommendationsSubtitle}>Not required, but recommended by us.</p>
            )}
          </Box>
        )}
        <Box className={classes.recommendations}>
          {recommendations.map(item => {
            const isExercise = item.type === 'EXERCISE';
            const stats = isExercise ? item.exerciseStats : item.evaluationStats;
            return (
              <Box className={classes.card} key={item.evaluationId || item.id}>
                <Box className={classes.evaluationWrapper}>
                  <Box className={classes.cardContent}>
                    <Box display="flex" alignItems="center" gap="8px">
                      <p className={classes.cardTitle}>{item.name}</p>
                      {isExercise && (
                        <Badge variant={badgeType.OUTLINED} style={badgeStyle.PRIMARY} className={classes.badge}>
                          Exercise
                        </Badge>
                      )}
                    </Box>
                    {stats && (
                      <p className={classes.cardSubtitle}>
                        {isExercise ? (
                          <>
                            {stats?.mainInstructions} Steps • {stats?.subInstructions} Sub Steps
                          </>
                        ) : (
                          <>
                            {stats?.totalCb} Questions • {stats?.totalRequiredCb} Required{' '}
                            {item.evalDurationMinutes && `• ${getTimeForEval(item.evalDurationMinutes)}`}
                          </>
                        )}
                      </p>
                    )}
                  </Box>
                </Box>
                {hasEvaluations ? (
                  <Menu
                    icon='more'
                    disabled={item.status === 'COMPLETED'}
                    className={classes.menu}
                    itemsWrapperClassName={classes.menuItemsWrapper}
                    items={[
                      {
                        label: `Reject ${isExercise ? 'exercise' : 'evaluation'}`,
                        icon: <Icons glyph='close-circle-outlined' className={classes.icon} />,
                        onClick: () => {
                          onRejectEvaluation(item);
                        },
                      },
                    ]}
                  />
                ) : (
                  <Button
                    variant={btnType.OUTLINE}
                    className={classes.useBtn}
                    onClick={() => {
                      onAddEvaluation([item.evaluationId || item.id]);
                    }}
                  >
                    Use
                  </Button>
                )}
              </Box>
            );
          })}
          {!allCompleted && (
            <Box className={classes.footer}>
              <p className={classes.footerTitle}>
                {hasEvaluations ? 'Missing something?' : 'Want to pick your own?'}
              </p>
              <Button variant={btnType.TEXT} className={classes.textLink} onClick={openEvaluationsDrawer}>
                Add Evaluation
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const EvaluationView = ({
  promptsVisible,
  openEvaluationsDrawer,
  onAddEvaluation,
  currentEvaluation,
  currentBlock,
  setTextInputInFocus,
  bodyRef,
  selectedIndex,
  leftTrigger,
  listHelper,
  rightTrigger,
  profileElements,
  eligibleForAutodoc,
  dctPreviousResponses,
  dctSelectedResponses,
  evaluationsCompleted,
  onPreviousResponseSelected,
  previousResponses,
  useAllPreviousResponses,
  onRejectEvaluation,
  expandedEvaluation,
  scrollToTop,
  setScrollToTop,
  aiMode,
  setAiMode,
}) => {
  const classes = useStyles();
  const { evaluations } = useSelector(selectAssignedEvaluationState);
  const allEvaluations = useSelector(selectEvaluation);
  
  const activeEvaluations = evaluations?.filter(ev => ev.status !== 'REJECTED' && ev.type === 'EVALUATION') || [];
  const activeExercises = evaluations?.filter(ev => ev.status !== 'REJECTED' && ev.type === 'EXERCISE') || [];
  const hasEvaluations = activeEvaluations.length > 0;
  const hasExercises = activeExercises.length > 0;
  const hasAny = hasEvaluations || hasExercises;
  
  const unassignedEvaluations = allEvaluations.filter(
    e => !evaluations?.map(ev => ev.id).includes(e.evaluationId),
  );
  const recommendations = hasAny
    ? evaluations.filter(ev => ev.status !== 'REJECTED' && !ev.isRecommended)
    : unassignedEvaluations.slice(0, 3);
  const { updateValues } = useEvaluationHead();
  let multiSelectElementValues = [];
  if (
    currentBlock?.cbType === EVALUATION_QUESTION_TYPES.MULTI_SELECT ||
    currentBlock?.cbType === EVALUATION_QUESTION_TYPES.SINGLE_SELECT
  ) {
    multiSelectElementValues = previousResponses[0]?.answers || [];
  }

  const dispatchUserResponse = (value, isUse = false) => {
    updateValues({ id: currentBlock.cbId, value, isUse });
  };

  const usePreviousResponse = () => {
    if (
      currentBlock.cbType === EVALUATION_QUESTION_TYPES.TEXT_INPUT ||
      currentBlock.cbType === EVALUATION_QUESTION_TYPES.RATING_SCALE
    ) {
      dispatchUserResponse(previousResponses[0]?.answers[0]?.value);
      currentBlock.evaluationResponses = [previousResponses[0]?.answers[0]?.value];
    } else if (
      currentBlock.cbType === EVALUATION_QUESTION_TYPES.SINGLE_SELECT ||
      currentBlock.cbType === EVALUATION_QUESTION_TYPES.MULTI_SELECT
    ) {
      const choices = currentBlock?.choices
        .filter(ch => {
          return multiSelectElementValues?.find(el => el.value === ch?.choice);
        })
        .map(ch => ch.choice);
      if (choices.length > 0) {
        if (currentBlock.cbType === EVALUATION_QUESTION_TYPES.SINGLE_SELECT) {
          dispatchUserResponse(choices[0]);
          currentBlock.evaluationResponses = [choices[0]];
        } else {
          choices.forEach((item, index) => {
            const isUse = index === 0 ? true : false;
            dispatchUserResponse(item, isUse);
          });
          currentBlock.evaluationResponses = choices;
        }
      }
    }
  };

  const toggleAiMode = () => {
    setAiMode(v => !v);
  };

  return (
    <Box className={classes.container}>
      {evaluationsCompleted ? (
        <Box className={classes.evaluationsBox}>
          <Box className={clsx(classes.titleBox, classes.bgWhite)}>
            <FireWorks className={classes.animatedIcon} />
            <Box display='flex' flexDirection='column' gap='4px' alignItems='center'>
              <p className={classes.sectionTitle}>
                Nice job!
              </p>
              <p className={clsx([classes.textAlignCenter, classes.sectionSubtitle])}>
                You’ve completed all assigned evaluations!
              </p>
            </Box>
          </Box>
          <EvaluationRecommendations
            allCompleted
            recommendations={unassignedEvaluations.slice(0, 3)}
            onAddEvaluation={onAddEvaluation}
            openEvaluationsDrawer={openEvaluationsDrawer}
          />
        </Box>
      ) : (
        <>
          {!currentEvaluation && (
            <Box className={classes.evaluationsBox}>
              <Box className={classes.welcomeFrame}>
                <Box display='flex' flexDirection='column' flexGrow={1} flex={1} gap='4px'>
                  <p className={classes.sectionTitle}>
                    {hasAny ? 'Collect Data' : 'Whoops!'}
                  </p>
                  <p className={classes.sectionSubtitle}>
                    {hasAny ? (
                      <>
                        <span>
                          {hasEvaluations && hasExercises
                            ? 'Here are the evaluations and exercises to complete.'
                            : hasEvaluations
                              ? 'Here are the evaluations to complete.'
                              : 'Here are the exercises to complete.'}
                        </span>
                        <span>Ensure they're relevant.</span>
                      </>
                    ) : (
                      `Looks like there are no assigned evaluations or exercises`
                    )}
                  </p>
                </Box>
                {hasAny ? (
                  <ElectroCardio className={classes.animatedIcon} />
                ) : (
                  <ThinkingAnimated className={classes.animatedIcon} />
                )}
              </Box>
              <EvaluationRecommendations
                recommendations={recommendations}
                hasEvaluations={hasAny}
                onAddEvaluation={onAddEvaluation}
                onRejectEvaluation={onRejectEvaluation}
                openEvaluationsDrawer={openEvaluationsDrawer}
              />
            </Box>
          )}
          {currentEvaluation && !currentBlock && (
            <EvaluationCover
              currentEvaluation={currentEvaluation}
              promptsVisible={promptsVisible}
              expandedEvaluation={expandedEvaluation}
              listHelper={listHelper}
              scrollToTop={scrollToTop}
              setScrollToTop={setScrollToTop}
            />
          )}
          {currentBlock && (
            <>
              {currentBlock.cbType === 'dct' ? (
                <DCTCover
                  currentBlock={currentBlock}
                  promptsVisible={promptsVisible}
                  dctPreviousResponses={dctPreviousResponses}
                  dctSelectedResponses={dctSelectedResponses}
                  onPreviousResponseSelected={onPreviousResponseSelected}
                  aiMode={aiMode}
                  listHelper={listHelper}
                  profileElements={profileElements}
                  eligibleForAutodoc={eligibleForAutodoc}
                  toggleAiMode={toggleAiMode}
                  setInputFocused={setTextInputInFocus}
                  useAllPreviousResponses={useAllPreviousResponses}
                  expandedEvaluation={expandedEvaluation}
                  scrollToTop={scrollToTop}
                  setScrollToTop={setScrollToTop}
                />
              ) : (
                <Question
                  currentBlock={currentBlock}
                  currentEvaluation={currentEvaluation}
                  promptsVisible={promptsVisible}
                  toggleAiMode={toggleAiMode}
                  setInputFocused={setTextInputInFocus}
                  bodyRef={bodyRef}
                  selectedIndex={selectedIndex}
                  profileElements={profileElements}
                  leftTrigger={leftTrigger}
                  rightTrigger={rightTrigger}
                  expandedEvaluation={expandedEvaluation}
                  listHelper={listHelper}
                  aiMode={aiMode}
                  previousResponses={previousResponses}
                  eligibleForAutodoc={eligibleForAutodoc}
                  usePreviousResponse={usePreviousResponse}
                  dispatchUserResponse={dispatchUserResponse}
                  scrollToTop={scrollToTop}
                  setScrollToTop={setScrollToTop}
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};
export { EvaluationView };
